import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Upload, Select, Row, Col, message, Modal, DatePicker, Grid, Card, Divider } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import 'antd/dist/antd.css';
import './crearRepartidor.css';
import Navbar from '../../components/navbarOut';
import Footer from '../../components/footer';
import Ciudades from '../../ciudades/ciudades'; // Importar el archivo de ciudades

const { Option } = Select;
const { useBreakpoint } = Grid;

/**
 * Componente para el registro de repartidores/conductores.
 * Maneja el formulario de registro completo con validaciones y subida de archivos.
 * @component
 */
const FormularioRegistroConductor = () => {
    const [form] = Form.useForm();
    const [image, setImage] = useState(null);
    const [documentImage, setDocumentImage] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState('');
    const [documentImagePreviewUrl, setDocumentImagePreviewUrl] = useState('');
    const navigate = useNavigate();

    /**
     * Maneja los cambios en los archivos subidos (fotos y documentos).
     * @param {Object} info - Información del archivo subido
     * @param {string} type - Tipo de archivo ('profile' o 'document')
     */
    const handleFileChange = (info, type) => {
        const lastFile = info.fileList.slice(-1)[0];
        if (lastFile) {
            if (type === 'profile') {
                setImage(lastFile.originFileObj);
                setImagePreviewUrl(URL.createObjectURL(lastFile.originFileObj));
            } else if (type === 'document') {
                setDocumentImage(lastFile.originFileObj);
                setDocumentImagePreviewUrl(URL.createObjectURL(lastFile.originFileObj));
            }
        } else {
            if (type === 'profile') {
                setImage(null);
                setImagePreviewUrl('');
            } else if (type === 'document') {
                setDocumentImage(null);
                setDocumentImagePreviewUrl('');
            }
        }
    };

    /**
     * Maneja el envío del formulario y registro del repartidor.
     * Valida campos, prepara FormData y realiza la petición al servidor.
     */
    const handleSubmit = async () => {
        form.validateFields().then(async (values) => {
            if (values.pais === 'colombia') {
                values.pais = 1;
            } else if (values.pais === 'mexico') {
                values.pais = 2;
            }
            console.log('Valores del formulario:', values);
            let formData = new FormData();
            
            // Format dates before sending
            if (values.fecha_vencimiento_soat) {
                formData.append('fecha_vencimiento_soat', values.fecha_vencimiento_soat.format('YYYY-MM-DD'));
            }
            if (values.fecha_vencimiento_tecnomecanica) {
                formData.append('fecha_vencimiento_tecnomecanica', values.fecha_vencimiento_tecnomecanica.format('YYYY-MM-DD'));
            }

            // Add file validation
            if (!documentImage) {
                message.error('Por favor sube una imagen del documento');
                return;
            }
            
            formData.append('foto_documento', documentImage);
            formData.append('nombre_usuario', values.nombre);
            formData.append('telefono', values.celular);
            formData.append('direccion', values.direccion);
            formData.append('documento_identidad', values.documento_identidad);
            formData.append('email', values.email);
            formData.append('pais', values.pais);
            if (image instanceof File) {
                formData.append('foto_cara', image);
            }
            formData.append('capacidad_carga', values.cantidad);
            formData.append('tipo_vehiculo', values.tipo_vehiculo);
            formData.append('placa_vehiculo', values.placa_vehiculo);
            formData.append('ciudad', values.ciudad);
            formData.append('password', values.password);
            formData.append('password_confirmation', values.password_confirmation);
            formData.append('id_rol', 4);
            formData.append('estado', 0);
            formData.append('tipo_identificacion', values.tipo_identificacion);
            formData.append('num_identificacion', values.num_identificacion);
            formData.append('numero_soat', values.numero_soat);
            formData.append('numero_tecnomecanica', values.numero_tecnomecanica);
            formData.append('latitud', parseFloat(values.latitud).toFixed(1));
            formData.append('longitud', parseFloat(values.longitud).toFixed(1));

            try {
                const response = await axios.post('https://api.99envios.app/api/auth/register_repartidor', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                console.log('Respuesta del registro:', response);
                if (response.status === 200 || response.status === 201) {
                    Modal.success({
                        title: 'Registro Exitoso',
                        content: 'Tu cuenta ha sido creada exitosamente y está lista para usar.',
                        onOk() {
                            navigate('/login'); // redirección a /login
                        }
                    });
                } else {
                    console.log('Registro no exitoso, estado:', response.status);
                    throw new Error('Registro no fue exitoso.');
                }
            } catch (error) {
                console.error('Error al registrar:', error);
                if (error.response && error.response.data && error.response.data.errors) {
                    const errors = error.response.data.errors;
                    if (errors.numero_tecnomecanica) {
                        message.error(errors.numero_tecnomecanica[0]);
                    }
                    if (errors.numero_soat) {
                        message.error(errors.numero_soat[0]);
                    }
                } else {
                    message.error('Error al registrar. Por favor, verifica tus datos y tu conexión.');
                }
            }
        }).catch(errorInfo => {
            console.error('Error en la validación del formulario:', errorInfo);
            message.error('Error en la validación del formulario. Revisa los errores.');
        }
        );

    };

    /**
     * Renderiza el formulario de registro con todos sus campos y validaciones.
     * @returns {JSX.Element} Formulario de registro de conductor.
     */

    const screens = useBreakpoint();
    
    // Función para determinar el span de las columnas según el tamaño de la pantalla
    const getColSpan = () => {
        if (screens.xl) return 8;      // 3 columnas (24/8 = 3) en pantallas grandes (≥1200px)
        if (screens.md) return 12;     // 2 columnas (24/12 = 2) en pantallas medianas (≥768px)
        return 24;                     // 1 columna en pantallas pequeñas (<768px)
    };

    const CustomCheckbox = ({ checked, onChange }) => (
        <div 
            className="custom-checkbox-container"
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                userSelect: 'none',
                padding: '8px 16px',
                border: '2px solid #6C63FF',
                borderRadius: '6px',
                backgroundColor: checked ? '#6C63FF' : 'white',
                color: checked ? 'white' : '#6C63FF',
                transition: 'all 0.3s',
                fontWeight: '500',
                marginBottom: '20px',
                fontSize: '12px'
            }}
        >
            <div 
                onClick={() => onChange({ target: { checked: !checked } })}
                style={{
                    width: '20px',
                    height: '20px',
                    border: checked ? '2px solid white' : '2px solid #6C63FF',
                    borderRadius: '4px',
                    marginRight: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: checked ? '#6C63FF' : 'white',
                    cursor: 'pointer'
                }}
            >
                {checked && (
                    <svg 
                        viewBox="0 0 24 24" 
                        width="14" 
                        height="14"
                        style={{ fill: 'white' }}
                    >
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                )}
            </div>
            <span>
                Acepto los{' '}
                <a href="https://99envios.com/politica-privacidad/" target='_blank' rel="noopener noreferrer">
                    términos y condiciones
                </a>
            </span>
        </div>
    );

    const renderFormFields = (screens) => {
        if (screens.xl) {
            return (
                <>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Form.Item name="tipo_identificacion" label="Tipo de Identificación" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona tipo">
                                    <Option value="CC">Cédula de Ciudadanía</Option>
                                    <Option value="CE">Cédula de Extranjería</Option>
                                    <Option value="TI">Tarjeta de Identidad</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="documento_identidad" label="Documento de Identidad" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa documento de identidad" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="num_identificacion" label="Número de Identificación" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu número de identificación" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu nombre" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="email" label="Correo electrónico" rules={[{ required: true, type: 'email' }]}>
                                <Input placeholder="Ingresa tu correo electrónico" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Form.Item name="pais" label="País" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona tu país">
                                    <Option value={1}>Colombia</Option>
                                    <Option value={2}>México</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="ciudad" label="Ciudad" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Selecciona tu ciudad"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {Ciudades.map(ciudad => (
                                        <Option key={ciudad.value} value={ciudad.value}>{ciudad.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="direccion" label="Dirección" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu dirección" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Form.Item name="celular" label="Celular" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el celular" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="password" label="Contraseña" rules={[{ required: true }]}>
                                <Input.Password placeholder="Ingresa tu contraseña" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="password_confirmation"
                                label="Confirmar contraseña"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Por favor confirma tu contraseña' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Las contraseñas no coinciden'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password placeholder="Confirma tu contraseña" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        } else if (screens.md) {
            return (
                <>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="tipo_identificacion" label="Tipo de Identificación" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona tipo">
                                    <Option value="CC">Cédula de Ciudadanía</Option>
                                    <Option value="CE">Cédula de Extranjería</Option>
                                    <Option value="TI">Tarjeta de Identidad</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="documento_identidad" label="Documento de Identidad" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa documento de identidad" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu nombre" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="num_identificacion" label="Número de Identificación" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu número de identificación" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="email" label="Correo electrónico" rules={[{ required: true, type: 'email' }]}>
                                <Input placeholder="Ingresa tu correo electrónico" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="pais" label="País" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona tu país">
                                    <Option value={1}>Colombia</Option>
                                    <Option value={2}>México</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="ciudad" label="Ciudad" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Selecciona tu ciudad"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {Ciudades.map(ciudad => (
                                        <Option key={ciudad.value} value={ciudad.value}>{ciudad.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="direccion" label="Dirección" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu dirección" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="celular" label="Celular" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el celular" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="password" label="Contraseña" rules={[{ required: true }]}>
                                <Input.Password placeholder="Ingresa tu contraseña" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="password_confirmation"
                                label="Confirmar contraseña"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Por favor confirma tu contraseña' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Las contraseñas no coinciden'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password placeholder="Confirma tu contraseña" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        } else {
            // 1-column layout para pantallas pequeñas: separar cada campo en fila distinta
            return (
                <>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="tipo_identificacion" label="Tipo de Identificación" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona tipo">
                                    <Option value="CC">Cédula de Ciudadanía</Option>
                                    <Option value="CE">Cédula de Extranjería</Option>
                                    <Option value="TI">Tarjeta de Identidad</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="documento_identidad" label="Documento de Identidad" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa documento de identidad" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="num_identificacion" label="Número de Identificación" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu número de identificación" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu nombre" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="pais" label="País" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona tu país">
                                    <Option value={1}>Colombia</Option>
                                    <Option value={2}>México</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="ciudad" label="Ciudad" rules={[{ required: true }]}>
                                <Select
                                    placeholder="Selecciona tu ciudad"
                                    showSearch
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {Ciudades.map(ciudad => (
                                        <Option key={ciudad.value} value={ciudad.value}>{ciudad.label}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="direccion" label="Dirección" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa tu dirección" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="celular" label="Celular" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el celular" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="email" label="Correo electrónico" rules={[{ required: true, type: 'email' }]}>
                                <Input placeholder="Ingresa tu correo electrónico" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="password" label="Contraseña" rules={[{ required: true }]}>
                                <Input.Password placeholder="Ingresa tu contraseña" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item
                                name="password_confirmation"
                                label="Confirmar contraseña"
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Por favor confirma tu contraseña' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Las contraseñas no coinciden'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password placeholder="Confirma tu contraseña" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        }
    };

    const renderVehicleFields = (screens) => {
        if (screens.xl) {
            // 3-column layout (sin cambios)
            return (
                <>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Form.Item name="placa_vehiculo" label="Placa del vehículo" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la placa del vehículo" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="numero_soat" label="Número de Soat" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el número de Soat" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="numero_tecnomecanica" label="Número de Tecnomecánica" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el número de Tecnomecánica" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Form.Item name="tipo_vehiculo" label="Tipo de vehículo" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona el tipo de vehículo">
                                    <Option value="moto">Moto</Option>
                                    <Option value="carro">Carro</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="fecha_vencimiento_soat" label="Vencimiento Soat" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="fecha_vencimiento_tecnomecanica" label="Vencimiento Tecnomecánica" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={8}>
                            <Form.Item name="cantidad" label="Capacidad de carga" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la capacidad de carga" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="latitud" label="Latitud" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la latitud" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="longitud" label="Longitud" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la longitud" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        } else if (screens.md) {
            // 2-column layout para pantallas medianas
            return (
                <>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="tipo_vehiculo" label="Tipo de vehículo" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona el tipo de vehículo">
                                    <Option value="moto">Moto</Option>
                                    <Option value="carro">Carro</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="placa_vehiculo" label="Placa del vehículo" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la placa del vehículo" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="numero_soat" label="Número de Soat" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el número de Soat" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="fecha_vencimiento_soat" label="Vencimiento Soat" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="numero_tecnomecanica" label="Número de Tecnomecánica" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el número de Tecnomecánica" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="fecha_vencimiento_tecnomecanica" label="Vencimiento Tecnomecánica" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={12}>
                            <Form.Item name="cantidad" label="Capacidad de carga" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la capacidad de carga" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="latitud" label="Latitud" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la latitud" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="longitud" label="Longitud" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la longitud" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        } else {
            // 1-column layout para pantallas pequeñas
            return (
                <>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="tipo_vehiculo" label="Tipo de vehículo" rules={[{ required: true }]}>
                                <Select placeholder="Selecciona el tipo de vehículo">
                                    <Option value="moto">Moto</Option>
                                    <Option value="carro">Carro</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="placa_vehiculo" label="Placa del vehículo" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la placa del vehículo" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="numero_soat" label="Número de Soat" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el número de Soat" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="fecha_vencimiento_soat" label="Vencimiento Soat" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="numero_tecnomecanica" label="Número de Tecnomecánica" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa el número de Tecnomecánica" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="fecha_vencimiento_tecnomecanica" label="Vencimiento Tecnomecánica" rules={[{ required: true }]}>
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="cantidad" label="Capacidad de carga" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la capacidad de carga" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="latitud" label="Latitud" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la latitud" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[24, 16]}>
                        <Col span={24}>
                            <Form.Item name="longitud" label="Longitud" rules={[{ required: true }]}>
                                <Input placeholder="Ingresa la longitud" />
                            </Form.Item>
                        </Col>
                    </Row>
                </>
            );
        }
    };

    return (
        <div className='contenedorConductor' style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'rgb(241, 239, 239)' }}>
            <Navbar />
            <div style={{ 
                flex: 1, 
                backgroundColor: 'rgb(241, 239, 239)', 
                display: 'flex', 
                flexDirection: 'column', 
                padding: '2rem',
                maxWidth: '1800px',
                margin: '0 auto',
                width: '100%'
            }}>
                <h2 style={{ 
                    color: 'white', 
                    textAlign: 'center', 
                    marginBottom: '24px', 
                    fontWeight: 'bold',
                    backgroundColor: '#6C63FF',
                    padding: '12px',
                    borderRadius: '8px'
                }}>
                    Registrarse como conductor
                </h2>
    
                <Form 
                    form={form} 
                    name="registro" 
                    layout="vertical" 
                    onFinish={handleSubmit}
                    style={{ width: '100%' }}
                >
                    <Card 
                        title="Información del Conductor" 
                        className="form-section"
                        headStyle={{ backgroundColor: '#6C63FF', color: 'white' }}
                        bodyStyle={{ padding: '24px' }}
                    >
                        {renderFormFields(screens)}
                        <Row gutter={[24, 16]} className="upload-row">
                            <Col span={screens.md ? 12 : 24}>
                                <Form.Item 
                                    name="foto_cara" 
                                    label="Foto de perfil"
                                    rules={[{ required: true, message: 'Por favor sube una foto de perfil' }]} // Se agregó validación requerida
                                    className="upload-container"
                                >
                                    <Upload
                                        listType="picture-card"
                                        showUploadList={false}
                                        beforeUpload={() => false}
                                        onChange={(info) => handleFileChange(info, 'profile')}
                                        className="upload-item"
                                    >
                                        {imagePreviewUrl ? (
                                            <div className="upload-preview">
                                                <img src={imagePreviewUrl} alt="avatar" />
                                            </div>
                                        ) : (
                                            <div className="upload-button">
                                                <UploadOutlined />
                                                <div style={{ marginTop: 8 }}>Seleccionar archivo</div>
                                            </div>
                                        )}
                                    </Upload>
                                </Form.Item>
                            </Col>
                            <Col span={screens.md ? 12 : 24}>
                                <Form.Item 
                                    name="foto_documento" 
                                    label="Imagen del Documento" 
                                    rules={[{ required: true }]}
                                    className="upload-container"
                                >
                                    <Upload
                                        listType="picture-card"
                                        showUploadList={false}
                                        beforeUpload={() => false}
                                        onChange={(info) => handleFileChange(info, 'document')}
                                        className="upload-item"
                                    >
                                        {documentImagePreviewUrl ? (
                                            <div className="upload-preview">
                                                <img src={documentImagePreviewUrl} alt="documento" />
                                            </div>
                                        ) : (
                                            <div className="upload-button">
                                                <UploadOutlined />
                                                <div style={{ marginTop: 8 }}>Seleccionar archivo</div>
                                            </div>
                                        )}
                                    </Upload>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>
    
                    <Divider />
    
                    {/* Vehicle Information Card */}
                    <Card 
                        title="Información del Vehículo" 
                        className="form-section"
                        headStyle={{ backgroundColor: '#6C63FF', color: 'white' }}
                        bodyStyle={{ padding: '24px' }}
                    >
                        {screens.xl || screens.md ? renderVehicleFields(screens) : renderVehicleFields(screens)}
                    </Card>

                    <div className="form-footer" style={{ textAlign: screens.md ? 'center' : 'left' }}>
                        <Form.Item 
                            name="terminos" 
                            valuePropName="checked" 
                            rules={[{ 
                                validator: (_, value) => value ? Promise.resolve() : Promise.reject('Debe aceptar los términos y condiciones') 
                            }]}
                            style={{ display: 'inline-block', marginRight: screens.md ? '20px' : '0' }}
                        >
                            <CustomCheckbox />
                        </Form.Item>
                        <Button 
                            type="primary" 
                            htmlType="submit" 
                            size="large" 
                            className="submit-button"
                            style={{ width: '100px', padding: '0 40px' }}
                        >
                            Registrar
                        </Button>
                    </div>
                </Form>
            </div>
            <Footer />
        </div>
    );
};

export default FormularioRegistroConductor;